import React from 'react';
import { motion } from 'framer-motion';
import { CrownIcon, XIcon } from 'lucide-react';
import { ProductName } from './ProductName';
import { FlowLine } from './FlowLine';
import { Ornament } from './Ornament';
const notThis = ['This is not a random collection of stretches.', 'This is not another text-heavy flexibility manual that gives you theory but leaves you responsible for building the actual class.', 'And it is not a folder full of disconnected routines you still have to organize yourself.'];
const builtAround = ['ready-to-teach classes', 'visual stretch sequences', 'start and end positions', 'body positioning', 'directional guidance', 'class timelines', 'hold durations', 'progressions', 'modifications', 'coaching cues', 'common errors', 'visual corrections', 'beginner-to-advanced development', 'full class structures'];
const usageSteps = ['Open the system.', 'Choose the class focus.', 'See the sequence and progression.', 'Teach.'];
export function SolutionReveal() {
  return <section className="bg-inverse px-4 py-20 text-inverse-foreground sm:py-28" data-id="element-295" data-mp="2bvfmexon0dvi">
      <div className="mx-auto max-w-4xl text-center" data-id="element-296" data-mp="2gth1z8mutv28">
        <motion.div initial={{
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.3,
        ease: [0.23, 1, 0.32, 1]
      }} data-id="element-297" data-mp="2kwn88g9yvm8p">
          <span className="mx-auto inline-flex h-14 w-14 items-center justify-center rounded-full border border-accent/60 text-accent" data-id="element-298" data-mp="1t60nb8sihytr">
            <CrownIcon size={24} strokeWidth={1.5} aria-hidden="true" data-id="element-299" data-mp="1x72o3u22uixu" />
          </span>
          <p className="mt-5 text-sm font-semibold uppercase tracking-[0.18em] text-inverse-foreground/70" data-id="element-300" data-mp="224clpwzme0wr">
            The complete <span className="text-accent-soft" data-id="element-301" data-mp="26yp8oh0b1ud9">6-in-1</span> visual
            teaching system for stretching instructors
          </p>
          <h2 className="mt-4 text-3xl font-semibold leading-tight text-inverse-foreground sm:text-5xl" data-id="element-302" data-mp="1eftd1gcnrm45">
            <ProductName script accentClassName="text-accent-soft" data-id="element-303" data-mp="2hqp8vd62ua28" />
          </h2>
          <Ornament className="mt-5" data-id="element-304" data-mp="2cse1d9398bn6" />
          <p className="mx-auto mt-5 max-w-3xl text-sm font-medium uppercase leading-7 tracking-[0.08em] text-inverse-foreground/70" data-id="element-305" data-mp="2crcpj2eezl4e">
            6 Complete Volumes · 150 Ready-to-Teach Classes · 100% Visual Core
            System · 5 Professional Bonus Guides
          </p>
        </motion.div>

        <div className="mt-12 grid gap-5 text-left md:grid-cols-2" data-id="element-306" data-mp="1ytefuab1gcv8">
          <div className="space-y-4 self-start rounded-xl border border-inverse-foreground/10 bg-inverse-foreground/[0.04] p-6" data-id="element-307" data-mp="1ttjoy6r9jna6">
            {notThis.map(item => <div key={item} className="flex gap-3 text-sm leading-6 text-inverse-foreground/75" data-id="element-308" data-mp="27qyinnojuh7y">
                <XIcon size={18} className="mt-0.5 shrink-0 text-accent-soft" aria-hidden="true" data-id="element-309" data-mp="3j7eznlfr55wx" />
                {item}
              </div>)}
          </div>
          <div className="rounded-xl border border-accent/40 bg-inverse-raised p-6" data-id="element-310" data-mp="3mouelo4nowu9">
            <p className="text-base font-medium leading-7 text-inverse-foreground" data-id="element-311" data-mp="3qtaen8rl9a1k">
              Stretching Instructor Ready is a complete 6-in-1 teaching system
              created specifically for instructors who want to plan less, teach
              with more structure and offer greater variety without rebuilding
              every class from scratch.
            </p>
            <p className="mt-4 text-sm font-semibold text-accent-soft" data-id="element-312" data-mp="e9anovrgjqid">
              The six-volume system is built around:
            </p>
            <ul className="mt-3 flex flex-wrap gap-1.5" data-id="element-313" data-mp="3voprbxdv0zwe">
              {builtAround.map(item => <li key={item} className="rounded-full border border-accent/30 bg-inverse-foreground/5 px-2.5 py-1 text-xs font-medium text-inverse-foreground" data-id="element-314" data-mp="4i67jsq1pzly">
                  {item}
                </li>)}
            </ul>
            <p className="mt-4 text-sm font-semibold uppercase leading-6 tracking-[0.06em] text-accent-soft" data-id="element-315" data-mp="34iad2zyn1vpc">
              And the core material is 100% visual.
            </p>
            <FlowLine tone="dark" className="mt-5" label="So instead of working through pages of unnecessary theory, you can:" steps={['See it', 'Understand it', 'Teach it']} data-id="element-316" data-mp="2vk8hx11l5x9z" />
            <ol className="mt-5 space-y-2.5" data-id="element-317" data-mp="3j9b0qc7yhw62">
              {usageSteps.map((step, index) => <li key={step} className="flex items-start gap-3 text-sm leading-6 text-inverse-foreground" data-id="element-318" data-mp="3eikrup0bnb6m" data-mpi={index}>
                  <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-accent text-[11px] font-bold text-foreground" data-id="element-319" data-mp="39j7uhabtqlps">
                    {index + 1}
                  </span>
                  {step}
                </li>)}
            </ol>
          </div>
        </div>
      </div>
    </section>;
}